/**
 * sign-consent — JIT consent step of the passwordless sign-in flow. Shown
 * only when the email is new — no `users/{uid}` doc exists yet. The display
 * name for the new `users/{uid}` doc arrives from verifyOTP's own return
 * (the invite's or the provisioned Auth account's `displayName`), never
 * typed here — this screen is a pure consent gate for an invited colleague.
 *
 * The terms are the one consent item: a checkbox whose label contains the
 * document name as a real link. The privacy notice is information, not
 * something agreed to (Art. 13 GDPR; processing rests on Art. 6(1)(b)), so it
 * is a plain line carrying the same kind of link. Each link is a genuine,
 * resolvable `/legal#<kind>` href (`legalAnchorHref`, legal.state.ts) that a
 * plain left click intercepts to open the document in an overlay, while a
 * modified click (⌘/ctrl/shift/a non-primary button, `isPlainLeftClick`)
 * falls through to the real navigation. Inside the terms checkbox the link
 * stops propagation, so neither click toggles the checkbox.
 *
 * Both documents resolve through `legalDocumentFor` (legal.state.ts,
 * wrapping `resolveLegalDocument` from @momo/i18n) — the organization's own
 * published override once fully published, else the platform default; on
 * the bare host, where no organization is open, the deployment operator's
 * identity and its own platform terms and privacy notice (`platform/legal`),
 * and without that record a refusal rather than an acceptance —
 * and this screen renders nothing until `legalContentState.settled`
 * (@momo/views): the override may still be mid-check the instant a
 * brand-new user reaches this step, and gating the render (never
 * `sessionReady`, never `needsConsent`, never a route guard — this stays a
 * local concern of this one screen) keeps what the user reads and what
 * SignConsentAcceptEvent records the same document, permanently.
 *
 * Dispatches:
 *   sign-consent-accept  — user accepted the terms; detail carries the
 *     RESOLVED tosVersion + privacyVersion — the terms version accepted and
 *     the privacy notice version shown: the organization's override, the
 *     platform default, or on the bare host the operator's platform record,
 *     and whether those texts were the operator's (`platform`)
 *   sign-consent-back    — the person chose a different email
 *
 * Try again, shown when the terms cannot be read, asks for a re-read through
 * the shared `legalRetry$` (legal.state.ts) rather than an event.
 *
 * Data down via properties (.email, .loading, .error, .invite). Intent up
 * via events.
 *
 * .invite — present only when sendOTP resolved a live pending invite for
 * this email; rendered as the same context banner sign-code shows, so the
 * invitee still sees what they're joining on this final step.
 */
import { gravity } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import type { LegalDocument } from "@momo/i18n";
import { html } from "lit";
import { property, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { takeUntil } from "rxjs";
import { LocalizeMixin } from "../i18n/localize.ts";
import { workspaceDomain } from "../workspace/workspace.state.ts";
import "../legal/legal-document.view.ts";
import {
	isPlainLeftClick,
	legalAnchorHref,
	legalContentState,
	legalDocumentFor,
	legalRetry$,
} from "../legal/legal.state.ts";
import type { InviteContext } from "./sign.state.ts";

export class SignConsentAcceptEvent extends CustomEvent<{
	tosVersion: string;
	privacyVersion: string;
	/** The texts shown were the deployment operator's (the bare host). */
	platform: boolean;
}> {
	static readonly eventName = "sign-consent-accept" as const;
	constructor(input: {
		tosVersion: string;
		privacyVersion: string;
		platform: boolean;
	}) {
		super(SignConsentAcceptEvent.eventName, {
			detail: input,
			bubbles: true,
			composed: true,
		});
	}
}

export class SignConsentBackEvent extends CustomEvent<void> {
	static readonly eventName = "sign-consent-back" as const;
	constructor() {
		super(SignConsentBackEvent.eventName, { bubbles: true, composed: true });
	}
}

@schmancyElement("sign-consent")
export class SignConsentStep extends LocalizeMixin(SchmancyElement) {
	@property({ type: String }) email = "";
	@property({ type: Boolean }) loading = false;
	@property({ type: String }) error = "";
	/** sendOTP's own invite-context projection — null on an ordinary sign-in. */
	@property({ attribute: false }) invite: InviteContext | null = null;

	@state() private tosChecked = false;

	/** Opens an already-resolved document in an overlay — resolution happens
	 *  once, in render(), so the overlay's content and the version this
	 *  screen later records off the same render are always the same text. */
	private openLegalDocument(
		document: LegalDocument,
		identityUnavailable: boolean,
	) {
		show(
			() => html`
				<legal-document
					.document=${document}
					?identityUnavailable=${identityUnavailable}
				></legal-document>
			`,
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}

	override render() {
		const legal = legalContentState.value;

		// The override may still be mid-check the instant a brand-new user
		// reaches this step — settle first, so the document the user reads and
		// checks the box for is never swapped out from under them before they
		// submit. See the module doc above; this gate is local to this screen.
		if (!legal.settled) {
			return html`
				<div class="flex items-center gap-3 px-4 py-6 sm:px-6" role="status" aria-live="polite">
					<schmancy-spinner size="sm" aria-hidden="true"></schmancy-spinner>
					<schmancy-typography type="body" token="md">${this.t("auth.signConsent.loadingTerms")}</schmancy-typography>
				</div>
			`;
		}

		// On the bare host with no operator record there is nobody to contract
		// with: sign-up is refused, and only the way back is offered.
		if (legal.operator === "absent") {
			return html`
				<div class="flex flex-col gap-4 px-4 py-6 sm:px-6">
					<schmancy-typography role="alert" type="body" token="md" class="text-error-default">${this.t("auth.signConsent.operatorMissing")}</schmancy-typography>
					<schmancy-button
						variant="text"
						class="self-start"
						size="md"
						?disabled=${this.loading}
						.busy=${this.loading}
						@click=${() => this.dispatchEvent(new SignConsentBackEvent())}
					>
						<schmancy-icon slot="prefix">arrow_back</schmancy-icon>
						${this.t("auth.signCode.useDifferentEmail")}
					</schmancy-button>
				</div>
			`;
		}

		const unavailable = this.t("legal.identityUnavailable");
		const {
			document: termsDoc,
			identityUnavailable: termsIdentityUnavailable,
		} = legalDocumentFor(
			"terms",
			this.uiLanguage,
			workspaceDomain.get(),
			unavailable,
		);
		const {
			document: privacyDoc,
			identityUnavailable: privacyIdentityUnavailable,
		} = legalDocumentFor(
			"privacy",
			this.uiLanguage,
			workspaceDomain.get(),
			unavailable,
		);

		// The organization's own text could not be read: nothing is offered for
		// consent rather than the default standing in for it.
		if (termsDoc === null || privacyDoc === null) {
			return html`
				<div class="flex flex-col gap-4 px-4 py-6 sm:px-6">
					<schmancy-typography role="alert" type="body" token="md" class="text-error-default">${this.t("auth.signConsent.termsUnavailable")}</schmancy-typography>
					<div class="flex flex-wrap items-center gap-2">
					<schmancy-button
						variant="filled"
						class="self-start"
						size="md"
						@click=${() => legalRetry$.next()}
					>
						${this.t("auth.signConsent.retry")}
					</schmancy-button>
					<schmancy-button
						variant="text"
						class="self-start"
						size="md"
						?disabled=${this.loading}
						.busy=${this.loading}
						@click=${() => this.dispatchEvent(new SignConsentBackEvent())}
					>
						<schmancy-icon slot="prefix">arrow_back</schmancy-icon>
						${this.t("auth.signCode.useDifferentEmail")}
					</schmancy-button>
					</div>
					${when(
						this.error,
						() =>
							html`<schmancy-typography role="alert" type="body" token="md" class="text-error-default">${this.error}</schmancy-typography>`,
					)}
				</div>
			`;
		}

		const canSubmit = this.tosChecked;
		const invite = this.invite;
		const inviteBanner = invite
			? this.t("auth.inviteContext.invitedTo", {
					workspaceName: invite.workspaceName,
					roleName: invite.roleName,
					invitedBy: invite.invitedBy,
				})
			: null;

		return html`
			<div class="flex flex-col gap-6 px-4 py-6 sm:px-6">

				<div ${gravity({ delay: 0, distance: 20 })} class="flex flex-col gap-1">
					<schmancy-typography type="headline" token="md">${this.t("auth.signConsent.title")}</schmancy-typography>
					<schmancy-typography type="body" token="md">
						${this.t("auth.signConsent.signingInAs")} <strong>${this.email}</strong>.
					</schmancy-typography>
				</div>

				${when(
					inviteBanner !== null,
					() => html`
						<div ${gravity({ delay: 30, distance: 16 })}>
							<schmancy-surface type="info" rounded="all" class="px-4 py-3 rounded-xl">
								<schmancy-typography type="body" token="sm">${inviteBanner}</schmancy-typography>
							</schmancy-surface>
						</div>
					`,
				)}

				<div ${gravity({ delay: 50, distance: 14 })} class="flex flex-col gap-3">
					<schmancy-checkbox
						.value=${this.tosChecked}
						?disabled=${this.loading}
						@change=${(e: CustomEvent<{ value: boolean }>) => {
							if (!this.loading) this.tosChecked = e.detail?.value ?? false;
						}}
					>
						<schmancy-typography type="body" token="sm">
							${this.t("auth.signConsent.agreeToThe")}
							<a
								href=${legalAnchorHref("terms")}
								class="font-semibold text-primary-default hover:underline"
								@click=${(e: MouseEvent) => {
									e.stopPropagation();
									if (!isPlainLeftClick(e)) return;
									e.preventDefault();
									this.openLegalDocument(termsDoc, termsIdentityUnavailable);
								}}
							>${this.t("legal.terms.title")}</a>.
						</schmancy-typography>
					</schmancy-checkbox>

					<schmancy-typography type="body" token="sm">
						${this.t("auth.signConsent.privacyNotice")}
						<a
							href=${legalAnchorHref("privacy")}
							class="font-semibold text-primary-default hover:underline"
							@click=${(e: MouseEvent) => {
								if (!isPlainLeftClick(e)) return;
								e.preventDefault();
								this.openLegalDocument(privacyDoc, privacyIdentityUnavailable);
							}}
						>${this.t("auth.signConsent.privacyNoticeLink")}</a>.
					</schmancy-typography>
				</div>

				<div class="grid" aria-live="polite">
					<schmancy-surface type="error" rounded="all" class="invisible col-start-1 row-start-1 rounded-xl px-4 py-3" aria-hidden="true">
						<schmancy-typography type="body" token="sm">${this.t("auth.signIn.accountCreationFailed")}</schmancy-typography>
					</schmancy-surface>
					<schmancy-surface type="error" rounded="all" class=${this.classMap({ "col-start-1 row-start-1 rounded-xl px-4 py-3": true, invisible: !this.error })} aria-hidden=${!this.error ? "true" : "false"}>
						<schmancy-typography type="body" token="sm" role="alert">${this.error}</schmancy-typography>
					</schmancy-surface>
				</div>

				<div ${gravity({ delay: 100, distance: 12 })}>
					<schmancy-button
						variant="filled"
						class="self-start"
						size="md"
						?disabled=${!canSubmit}
						.busy=${this.loading}
						@click=${() => {
							if (!canSubmit || this.loading) return;
							this.dispatchEvent(
								new SignConsentAcceptEvent({
									tosVersion: termsDoc.version,
									privacyVersion: privacyDoc.version,
									platform: legal.operator !== undefined,
								}),
							);
						}}
					>
						${this.t("auth.signConsent.continue")}
					</schmancy-button>
				</div>

			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"sign-consent": SignConsentStep;
	}
}
